import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import {
  useCallback,
  type FunctionComponent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import {
  RIGHT_DOCK_MAX_WIDTH,
  RIGHT_DOCK_MIN_WIDTH,
  TITLE_HEIGHT,
} from "../constants";

/**
 * Width of the column.
 *
 * The upper bound is wider than the feature panel on the left because what
 * goes in here is made of stacked fractions rather than lines of text. It
 * gives way on a narrow window though: with the left column open too, a fixed
 * 360px would squeeze the canvas down to a couple of nodes, and the canvas is
 * what the user has to keep clicking.
 */
const dockWidth = `clamp(${RIGHT_DOCK_MIN_WIDTH}px, 30vw, ${RIGHT_DOCK_MAX_WIDTH}px)`;

interface RightPanelTab<TabId extends string> {
  id: TabId;
  label: string;
  content: ReactNode;
}

interface RightPanelDockProps<TabId extends string> {
  tabs: Array<RightPanelTab<TabId>>;
  activeTabId: TabId;
  onTabChange: (tabId: TabId) => void;
  onClose: () => void;
}

/**
 * The column to the right of the canvas.
 *
 * It holds the panels that comment on whatever the user is doing on the canvas
 * rather than the ones the user goes to. Those have to stay open while the
 * canvas is being clicked, which is exactly what a floating panel can't do: it
 * would cover the nodes it's talking about.
 *
 * The panels are tabbed rather than stacked because they're alternative
 * readings of the same thing, the derivative of the selected node, and nobody
 * needs two of those at once. Tabbed, whichever is in front gets the whole
 * column.
 *
 * Which tab is in front is the caller's, not this component's: two of the tabs
 * change what the canvas itself shows, so the owner of the canvas has to be the
 * one that knows.
 */
const RightPanelDock = <TabId extends string>({
  tabs,
  activeTabId,
  onTabChange,
  onClose,
}: RightPanelDockProps<TabId>): ReturnType<FunctionComponent> => {
  const handleTabChange = useCallback(
    (event: SyntheticEvent, tabId: TabId): void => {
      onTabChange(tabId);
    },
    [onTabChange],
  );

  const getTabId = useCallback((id: string): string => {
    return `right-panel-tab-${id}`;
  }, []);

  const getTabPanelId = useCallback((id: string): string => {
    return `right-panel-tabpanel-${id}`;
  }, []);

  return (
    <Box
      height={`calc(100vh - ${TITLE_HEIGHT}px)`}
      display="flex"
      flexDirection="column"
      bgcolor="background.paper"
      sx={{
        width: dockWidth,
        // The canvas is what gives way, not the column
        flexShrink: 0,
      }}
    >
      {/* Tab bar */}
      <Box
        display="flex"
        alignItems="center"
        borderBottom={1}
        borderColor="divider"
        flexShrink={0}
      >
        <Tabs
          value={activeTabId}
          onChange={handleTabChange}
          variant="fullWidth"
          aria-label="right-panel-tabs"
          sx={{ flexGrow: 1, minHeight: 0 }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={tab.label}
              id={getTabId(tab.id)}
              aria-controls={getTabPanelId(tab.id)}
              sx={{ minWidth: 0, px: 1 }}
            />
          ))}
        </Tabs>

        {/* Closing the column is what puts the canvas back to full width, so
        it belongs here next to the tabs rather than inside one of them */}
        <Tooltip title="Close">
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="close-right-panel"
            sx={{ mx: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* The panel in front takes the whole column. The others stay mounted so
      that switching back keeps the scroll position and whatever the user had
      folded */}
      {tabs.map((tab) => (
        <Box
          key={tab.id}
          role="tabpanel"
          hidden={tab.id !== activeTabId}
          id={getTabPanelId(tab.id)}
          aria-labelledby={getTabId(tab.id)}
          display={tab.id === activeTabId ? "flex" : "none"}
          flexDirection="column"
          flexGrow={1}
          sx={{ minHeight: 0 }}
        >
          {tab.content}
        </Box>
      ))}
    </Box>
  );
};

export default RightPanelDock;
export type { RightPanelTab };
